import React from "react";
import type { BikeImpressions } from "../../types/impressionJob";
import { TrendingUp, Calendar, Bike, BarChart3 } from "lucide-react";

interface JobAnalyticsSummaryProps {
  analytics: BikeImpressions[];
  totalImpressions: number;
  rowCount: number;
}

export const JobAnalyticsSummary: React.FC<JobAnalyticsSummaryProps> = ({
  analytics,
  totalImpressions,
  rowCount,
}) => {
  // Calculate stats
  const bikeCount = analytics.length;
  const totalDays = new Set(
    analytics.flatMap((bike) => bike.daily_impressions.map((day) => day.date))
  ).size;

  const avgImpressionsPerBike = bikeCount > 0 ? totalImpressions / bikeCount : 0;
  const avgImpressionsPerDay = totalDays > 0 ? totalImpressions / totalDays : 0;

  // Find top performing bike
  const topBike = analytics.reduce(
    (top, bike) => {
      const bikeTotal = bike.daily_impressions.reduce(
        (sum, day) => sum + day.total_impressions,
        0
      );
      return bikeTotal > top.impressions
        ? { bike_id: bike.bike_id, impressions: bikeTotal }
        : top;
    },
    { bike_id: "", impressions: 0 }
  );

  // Find peak day
  const allDays = analytics.flatMap((bike) =>
    bike.daily_impressions.map((day) => ({
      date: day.date,
      impressions: day.total_impressions,
    }))
  );
  const dayTotals = allDays.reduce((acc, day) => {
    acc[day.date] = (acc[day.date] || 0) + day.impressions;
    return acc;
  }, {} as Record<string, number>);

  const peakDay = Object.entries(dayTotals).reduce(
    (peak, [date, impressions]) =>
      impressions > peak.impressions ? { date, impressions } : peak,
    { date: "", impressions: 0 }
  );

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">
                Total Impressions
              </p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                {totalImpressions.toLocaleString()}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">
                Bikes Tracked
              </p>
              <p className="text-3xl font-bold text-green-900 mt-2">
                {bikeCount}
              </p>
            </div>
            <Bike className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">
                Days Tracked
              </p>
              <p className="text-3xl font-bold text-purple-900 mt-2">
                {totalDays}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">
                Data Points
              </p>
              <p className="text-3xl font-bold text-orange-900 mt-2">
                {rowCount.toLocaleString()}
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-sm font-medium text-gray-600">
            Avg. Impressions per Bike
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {avgImpressionsPerBike.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-sm font-medium text-gray-600">
            Avg. Impressions per Day
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {avgImpressionsPerDay.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-sm font-medium text-gray-600">
            Top Performing Bike
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {topBike.bike_id || "N/A"}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {topBike.impressions.toLocaleString()} impressions
          </p>
        </div>
      </div>

      {/* Peak Performance */}
      {peakDay.date && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Peak Performance Day
          </h3>
          <div className="flex items-center gap-4">
            <div>
              <p className="text-sm text-gray-600">Date</p>
              <p className="text-xl font-bold text-gray-900">
                {new Date(peakDay.date).toLocaleDateString()}
              </p>
            </div>
            <div className="h-8 w-px bg-gray-300" />
            <div>
              <p className="text-sm text-gray-600">Impressions</p>
              <p className="text-xl font-bold text-indigo-600">
                {peakDay.impressions.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
